import React, {useEffect, useMemo, useState} from "react";
import {Button, Dropdown, Menu, Tabs} from "antd";
import TabPane from "antd/es/tabs/TabPane";
import {Contain} from "@components/layout";
import {formatNumber} from "@lib/format";
import {DownOutlined, SearchOutlined} from '@ant-design/icons';
import {BoxesAddDrawer} from "@components/boxes-add-drawer";
import MyCollectionIssues from "@mwPages/my-collection/issues/MyCollectionIssues";
import MyCollectionBoxes from "@mwPages/my-collection/boxes/MyCollectionBoxes";
import useSearchView from "@hooks/useSearchView";
import MyCollectionSeries from "@mwPages/my-collection/series/MyCollectionSeries";
import WishlistIssues from "@mwPages/my-collection/wishlist/WishlistIssues";
import {useGetCollectionOverviewQuery} from "@mwServices/User";
import useSubscriptionType from "@hooks/useSubscriptionType";
import DebounceInput from "@components/debounce-input/DebounceInput";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {useNavigate} from "react-router-dom";

const MyCollectionTabs = ({ issueFilters, setIssueFilters }) => {
    const windowDimensions = useWindowSize();
    const navigate = useNavigate();
    const { setView, view } = useSearchView();
    const [addBox, setAddBox] = useState<{show: boolean, issue_ids: number[], series_ids: number[]}>({show: false, issue_ids: [], series_ids: []});
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState<string>(view ? view : 'issues');
    const overviewQuery = useGetCollectionOverviewQuery();
    const [firstLoad, setFirstLoad] = useState(true);

    const subscriptionType = useSubscriptionType();

    const searchPlaceholder = useMemo(() => {
        switch(activeTab){
            case 'boxes':
                return 'Search boxes...'
            case 'series':
                return 'Search series...'
            case 'wishlist':
                return 'Search wishlist...'
            case 'issues':
            default:
                return 'Search collection...'
        }
    }, [activeTab]);

    useEffect(() => {
        if(view && view !== activeTab) {
            setActiveTab(view);
        }
    }, [view]);
    useEffect(() => {
        if(view !== activeTab) {
            setView(activeTab);
        }
        if(activeTab !== 'issues'){
            setIssueFilters(null);
        }
    }, [activeTab]);

    useEffect(() => {
        if(issueFilters && activeTab !== 'issues'){
            setActiveTab('issues');
        }
    }, [issueFilters]);

    const searchCollection = (newQuery) => {
        setSearch(newQuery);
        setActiveTab('issues');
    };

    useEffect(() => {
        if(firstLoad){
            setFirstLoad(false);
        }
    }, [search]);

    return (
        <Contain className={"myCollectionIssues "+(windowDimensions.up(SCREEN.MD) ? 'large-screen' : '')} >
            <Tabs
                activeKey={activeTab}
                onTabClick={(newTab) => {
                    if(!subscriptionType.isUnlimitedOrTrial && (newTab === 'boxes' ||  newTab === 'series')) {
                        navigate(`/restricted-access`);
                    } else {
                        setSearch("");
                        setActiveTab(newTab);
                    }
                }}
                tabBarExtraContent={{
                    right: <div className="rightTabContent">
                        {((subscriptionType.isUnlimitedOrTrial && (activeTab === 'boxes')) || activeTab !== 'boxes') && (
                            <>
                                <DebounceInput
                                    size="large"
                                    placeholder={searchPlaceholder}
                                    className="searchInput"
                                    prefix={<SearchOutlined />}
                                    value={search}
                                    maxLength={100}
                                    debounceTimeout={2000}
                                    onChange={(event) => {
                                        const { value } = event.target;
                                        if(value !== search) setSearch(value);
                                    }}
                                />

                                {(activeTab === 'issues' || activeTab === 'series') && ( <Button size="large" className="greyButton addButton" data-toggle="header-search-overlay">+ Add</Button>)}
                                {activeTab === 'boxes' && ( <Button size="large" className="greyButton addButton" onClick={() => setAddBox({show: true, issue_ids: [], series_ids: []})}>+ Add</Button>)}
                                {activeTab === 'wishlist' && ( <Button size="large" className="greyButton addButton" data-toggle="header-search-overlay">+ Add</Button>)}
                                <Dropdown className="greyButton selectButton" trigger={["click"]} overlay={(
                                    <Menu >
                                        <Menu.Item key={0} data-toggle="header-search-overlay">Add Comic</Menu.Item>
                                        {subscriptionType.isUnlimitedOrTrial && (<Menu.Item key={1}  onClick={() => setAddBox({show: true, issue_ids: [], series_ids: []})}>Add Virtual Box</Menu.Item>)}
                                    </Menu>
                                )}>
                                    <DownOutlined />
                                </Dropdown>
                            </>
                        )}
                    </div>,
                }}
            >
                <TabPane tab={"All (" + (overviewQuery.data ? formatNumber(overviewQuery.data.info.items) : 0) + ")"} key="issues">
                    {activeTab === 'issues' && (
                        <MyCollectionIssues
                            search={search}
                            setAddBox={setAddBox}
                            setSearch={setSearch}
                            searchPlaceholder={searchPlaceholder}
                            issueFilters={issueFilters}
                            setIssueFilters={setIssueFilters}
                            collectionOverviewInfo={overviewQuery.data ? overviewQuery.data.info : null}
                        />
                    )}
                </TabPane>
                <TabPane tab={"Series (" + (overviewQuery.data ? formatNumber(overviewQuery.data.info.series_count) : 0) + ")"} key="series">
                    {activeTab === 'series' && (
                        <MyCollectionSeries search={search} setSearch={setSearch} setAddBox={setAddBox} />
                    )}
                </TabPane>
                <TabPane tab={"Virtual Boxes (" + (overviewQuery.data ? formatNumber(overviewQuery.data.info.box_count) : 0) + ")"} key="boxes">
                    {activeTab === 'boxes' && (
                        <MyCollectionBoxes search={search} setSearch={setSearch} />
                    )}
                </TabPane>
                <TabPane tab={"Wishlist (" + (overviewQuery.data ? formatNumber(overviewQuery.data.info.items_wishlist) : 0) + ")"} key="wishlist">
                    {activeTab === 'wishlist' && (
                        <WishlistIssues
                            search={search}
                            setSearch={setSearch}
                            searchPlaceholder={searchPlaceholder}
                            searchCollection={searchCollection}
                        />
                    )}
                </TabPane>
            </Tabs>

            <BoxesAddDrawer visible={addBox.show} issue_ids={addBox.issue_ids} series_ids={addBox.series_ids} onClose={() => {setAddBox({show: false, issue_ids: [], series_ids: []})}} />
        </Contain>
    );
};

export default MyCollectionTabs;